/**
 * API error normalisation, ported from apps/web/src/services/errorService.ts
 * and apps/web/src/utils/errorHandling.ts (extractFieldErrors).
 *
 * A failed request always becomes an `ApiError` whose `message` is safe to show
 * a user. The message is resolved in this order (parity §0):
 *   1. a known `detail.error_code`;
 *   2. the backend's `detail.message`, or a plain-text `detail` (FastAPI's
 *      `HTTPException(detail="…")`), except Starlette's generic status phrases;
 *   3. a per-HTTP-status message;
 *   4. a network / timeout / generic message.
 *
 * Cancellation is NOT an ApiError: an aborted request rethrows the original
 * `AbortError` so request owners (and TanStack Query) can discard it silently.
 */

export const ERROR_CODE_MESSAGES: Readonly<Record<number, string>> = {
  503006: 'You don’t have enough credits for this action. Add credits in Billing.',
  401001: 'Incorrect credentials. Please check your email and password and try again.',
  403001: "You don't have permission to perform this action.",
  403002: 'This feature is not available in your current plan.',
  403004: 'Sample data is read-only. Connect your ad networks to work with your own.',
  429001: 'Too many requests. Please wait a moment before trying again.',
  429002: 'Daily API limit exceeded. Please try again tomorrow or upgrade your plan.',
  400001: 'Check your entries and try again.',
  400002: 'Complete the required fields.',
  404001: 'The requested resource was not found.',
  404002: 'Chat not found or has been deleted.',
  404003: 'Message not found or has been deleted.',
  500001: 'We couldn’t complete this action. Try again in a few minutes.',
  500002: 'Service temporarily unavailable. Please try again in a few minutes.',
  422001: 'Invalid asset type or dimensions specified.',
  422002: 'Prompt is too long. Please shorten your request.',
  422003: 'Unsupported file format. Please use supported image/video formats.',
  413001: 'File size too large. Please upload a smaller file.',
  413002: 'Too many files uploaded. Please reduce the number of files.',
};

export const STATUS_MESSAGES: Readonly<Record<number, string>> = {
  400: 'Invalid request. Please check your input and try again.',
  401: 'Authentication required. Please log in.',
  403: "Access denied. You don't have permission for this action.",
  404: 'Resource not found.',
  413: 'File too large. Please upload a smaller file.',
  429: 'Too many requests. Please wait before trying again.',
  500: 'Server error. Please try again later.',
  502: 'Service temporarily unavailable. Please try again.',
  503: 'Service temporarily unavailable. Please try again.',
};

export const FALLBACK_MESSAGE = 'We couldn’t complete this request. Try again.';
export const NETWORK_MESSAGE = 'Network error. Please check your connection and try again.';
export const TIMEOUT_MESSAGE = 'Request timeout. Please try again.';
export const UNEXPECTED_MESSAGE = 'An unexpected error occurred. Please try again.';

/** Codes that are ordinary user outcomes, not defects worth logging. */
const NON_LOGGABLE_CODES = new Set([400001, 400002, 401001, 403001, 403002, 403003, 403004]);

export type ApiErrorKind = 'http' | 'network' | 'timeout';

export class ApiError extends Error {
  override readonly name = 'ApiError';
  /** HTTP status; 0 for network failures and timeouts. */
  readonly status: number;
  /** Backend `detail.error_code`, when it sent one. */
  readonly code: number | undefined;
  readonly kind: ApiErrorKind;
  /** FastAPI 422 validation messages keyed by field name, or null. */
  readonly fieldErrors: Readonly<Record<string, string>> | null;
  /** The raw `detail` payload, for callers that need more than the message. */
  readonly detail: unknown;

  constructor(init: {
    message: string;
    status: number;
    kind: ApiErrorKind;
    code?: number | undefined;
    fieldErrors?: Record<string, string> | null;
    detail?: unknown;
  }) {
    super(init.message);
    this.status = init.status;
    this.kind = init.kind;
    this.code = init.code;
    this.fieldErrors = init.fieldErrors ?? null;
    this.detail = init.detail;
  }

  get shouldLog(): boolean {
    return this.code === undefined || !NON_LOGGABLE_CODES.has(this.code);
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Starlette's default phrases (http.HTTPStatus): the per-status messages below say more. */
const GENERIC_DETAILS = new Set([
  'Bad Gateway',
  'Bad Request',
  'Conflict',
  'Content Too Large',
  'Forbidden',
  'Gateway Timeout',
  'Gone',
  'Internal Server Error',
  'Method Not Allowed',
  'Not Acceptable',
  'Not Found',
  'Not Implemented',
  'Payload Too Large',
  'Payment Required',
  'Request Timeout',
  'Service Unavailable',
  'Too Many Requests',
  'Unauthorized',
  'Unprocessable Content',
  'Unprocessable Entity',
  'Unsupported Media Type',
]);

/** Read `detail.error_code` / `detail.message`, or a plain-text `detail`, from a FastAPI error body. */
const structuredDetail = (detail: unknown): { code?: number; message?: string } => {
  if (typeof detail === 'string') {
    const text = detail.trim();
    return text && !GENERIC_DETAILS.has(text) ? { message: text } : {};
  }
  if (!isRecord(detail)) return {};
  const code = typeof detail.error_code === 'number' ? detail.error_code : undefined;
  const message =
    typeof detail.message === 'string' && detail.message.length > 0 ? detail.message : undefined;
  return { ...(code !== undefined ? { code } : {}), ...(message !== undefined ? { message } : {}) };
};

/**
 * FastAPI 422s name the offending field; show the message on that field rather
 * than in a toast. Returns null when the error is not a field-level failure.
 */
export const extractFieldErrors = (
  status: number,
  detail: unknown,
): Record<string, string> | null => {
  if (status !== 422 || !Array.isArray(detail)) return null;
  const map: Record<string, string> = {};
  for (const item of detail as unknown[]) {
    if (!isRecord(item)) continue;
    const loc = Array.isArray(item.loc) ? (item.loc as unknown[]) : [];
    const field = loc.length ? String(loc[loc.length - 1]) : '';
    if (field && typeof item.msg === 'string') map[field] = item.msg;
  }
  return Object.keys(map).length ? map : null;
};

/** Build the ApiError for an HTTP response whose body has been parsed. */
export const apiErrorFromResponse = (status: number, body: unknown): ApiError => {
  const detail = isRecord(body) ? body.detail : undefined;
  const { code, message: backendMessage } = structuredDetail(detail);
  const known = code !== undefined ? ERROR_CODE_MESSAGES[code] : undefined;
  const message = known ?? backendMessage ?? STATUS_MESSAGES[status] ?? FALLBACK_MESSAGE;
  return new ApiError({
    message,
    status,
    kind: 'http',
    code,
    fieldErrors: extractFieldErrors(status, detail),
    detail,
  });
};

export const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException
    ? error.name === 'AbortError'
    : error instanceof Error && error.name === 'AbortError';

/** A user-facing message for anything thrown by a request or a mutation. */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) return error.message;
  if (isAbortError(error)) return 'Request was cancelled.';
  if (error instanceof Error && error.message) return error.message;
  return UNEXPECTED_MESSAGE;
};
